// React
import { ReactElement, useEffect, useState } from 'react';
// Styles
import styles from './bus-reservation.module.scss';
import { useTranslation } from 'next-i18next';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { Button } from '@utmb/design-system/button/Button';
import { Font } from '@utmb/design-system/text/font';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { useRouter } from 'next/router';
import * as React from 'react';
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
import ShuttleLineView from '@utmb/types/view/ShuttleLineView';
import BookingBusPassShuttleDto from '@utmb/types/dto/BookingBusPassShuttleDto';
import { toast } from 'react-toastify';
import { useUser } from '@utmb/contexts/AuthContext';
import Spinner from '@utmb/design-system/icons/svgs/Spinner';
import { PdfRunnerShuttleButton } from './PdfRunnerShuttle';
import { useGlobal } from '@utmb/contexts/GlobalContext';
import RunnerShuttlesView from '@utmb/types/view/RunnerShuttlesView';
import { OpenStatusEnum } from '@utmb/types/enum/OpenStatusEnum';

interface BusReservationRunnerProps {
    raceYearId: number;
    tenant: string;
    year: number;
}

export const BusReservationRunner = (props: BusReservationRunnerProps): ReactElement => {
    const { t } = useTranslation();
    const { year } = props;
    const { locale } = useRouter();
    const [shuttleLines, setShuttleLines] = useState<ShuttleLineView[] | undefined>();
    const [origShuttleLines, setOrigShuttleLines] = useState<ShuttleLineView[] | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [isModifying, setIsModifying] = useState(false);
    const [canModify, setCanModify] = useState<OpenStatusEnum>(OpenStatusEnum.CLOSED);
    const { getToken } = useUser();
    const {
        global: { eventsTopBar },
    } = useGlobal();
    const eventView = eventsTopBar.find((event) => event.tenant === props.tenant);

    async function getBusInformation(): Promise<void> {
        setIsLoading(true);

        const token = await getKeycloakToken();

        const busInformation: RunnerShuttlesView = (await fetchTheAPI({
            tenantId: props.tenant,
            isClient: true,
            bearer: token,
            path: `/bus/shuttle/${props.raceYearId}`,
            params: { lang: locale as string },
        })) as RunnerShuttlesView;

        setCanModify(busInformation.runnerShuttleOpen);
        if (busInformation.shuttleLines) {
            setShuttleLines(busInformation.shuttleLines);
            busInformation.shuttleLines.map((shuttleLine) => {
                if (shuttleLine.selectedShuttleId === undefined && busInformation.runnerShuttleOpen === OpenStatusEnum.OPEN) setIsModifying(true);
            });
            const clone: ShuttleLineView[] = JSON.parse(JSON.stringify(busInformation));
            setOrigShuttleLines(clone);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        getBusInformation().then();
    }, []);

    const handleReservation = async (values: { shuttleLines: ShuttleLineView[] | undefined }) => {
        const shuttlesArray: BookingBusPassShuttleDto[] = [];

        values?.shuttleLines?.map((shuttleLine, index) => {
            const shuttleData: BookingBusPassShuttleDto = { shuttleLineId: shuttleLine.id, shuttleId: shuttleLine.selectedShuttleId ?? 0 };
            if (origShuttleLines && origShuttleLines[index]?.selectedShuttleId !== shuttleLine.selectedShuttleId) shuttlesArray.push(shuttleData);
        });

        shuttlesArray.map((shuttle: BookingBusPassShuttleDto) => {
            handleReservationRunner(shuttle);
        });
    };

    async function handleReservationRunner(shuttle: BookingBusPassShuttleDto, cancel = false): Promise<void> {
        const token = await getToken();
        setIsLoading(true);

        await fetchTheAPI({
            path: `/bus/shuttle/${props.raceYearId}/${shuttle.shuttleLineId}/${shuttle.shuttleId}`,
            tenantId: props.tenant,
            bearer: token,
            method: FetchMethodsEnum.POST,
            params: cancel ? { cancel: 'true' } : undefined,
        })
            .then((result: any) => {
                if (result === null) {
                    toast.success('Saved');
                } else if ((result.statusCode === 400 || result.statusCode === 404 || result.statusCode === 409) && result.message) {
                    toast.error(result.message);
                } else {
                    toast.error(result);
                }
            })
            .catch((error) => {
                toast.error(error);
            });
        getBusInformation().then(() => {
            setIsModifying(false);
            setIsLoading(false);
        });
    }

    async function handleCancelReservation(): Promise<void> {
        if (!confirm(t('busReservation.shuttleRunner.confirmCancel'))) return;
        const linesToCancel = (shuttleLines ?? []).filter(
            (sl) => sl.selectedShuttleId !== undefined && sl.selectedShuttleId !== 0 && sl.selectedShuttleId !== null
        );
        for (const line of linesToCancel) {
            await handleReservationRunner({ shuttleLineId: line.id, shuttleId: 0 }, true);
        }
    }

    return (
        <>
            {isLoading && <Spinner />}
            {!isLoading && shuttleLines && shuttleLines?.length > 0 && (
                <section className={styles.shuttleBooking}>
                    <Font.FuturaBold desktop="18" mobile="16" className={styles.informationRunner}>
                        {t(
                            `busReservation.shuttleRunner.title${
                                isModifying || (canModify === OpenStatusEnum.CLOSED && shuttleLines[0].selectedShuttleId === undefined) ? '' : 'Chosen'
                            }`
                        )}
                    </Font.FuturaBold>
                    {!isModifying ? (
                        <div className={styles.form}>
                            {shuttleLines.map((shuttleLine, index) => (
                                <div key={`row${index}`} className={styles.formRow}>
                                    <div className={styles.formItem}>
                                        <div className={styles.input}>
                                            <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                {t(`busReservation.shuttleLine.${shuttleLine.type}`)}
                                            </Font.OxaniumRegular>
                                            <Font.OxaniumRegular as="span" mobile="14" desktop="16">
                                                {shuttleLine.name}
                                            </Font.OxaniumRegular>
                                        </div>
                                    </div>
                                    <div className={styles.formItem}>
                                        <div className={styles.input}>
                                            <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                {t('busReservation.shuttle')}
                                            </Font.OxaniumRegular>
                                            <Font.OxaniumRegular as="span" mobile="14" desktop="16">
                                                {shuttleLine.selectedShuttleId === undefined
                                                    ? t('busReservation.shuttleRunner.notChosen')
                                                    : shuttleLine.selectedShuttleId === 0 || shuttleLine.selectedShuttleId === null
                                                    ? t('busReservation.empty')
                                                    : `${shuttleLine.shuttles.find((shuttle) => shuttle.id === shuttleLine.selectedShuttleId)?.name ?? ''} ${
                                                          shuttleLine.shuttles.find((shuttle) => shuttle.id === shuttleLine.selectedShuttleId)?.schedule ??
                                                          t('busReservation.empty')
                                                      }`}
                                            </Font.OxaniumRegular>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className={styles.formRow}>
                                <div className={styles.formItem}>
                                    {canModify === OpenStatusEnum.OPEN ? (
                                        <div className={styles.shuttleSelector}>
                                            {shuttleLines.some(
                                                (sl) => sl.selectedShuttleId !== undefined && sl.selectedShuttleId !== 0 && sl.selectedShuttleId !== null
                                            ) && (
                                                <Button.Default
                                                    color={getColorStyle(ColorTypesEnum.WHITE)}
                                                    bgcolor={getColorStyle(ColorTypesEnum.RED)}
                                                    label={t('busReservation.shuttleRunner.cancelChoice')}
                                                    className={styles.button}
                                                    onPress={() => handleCancelReservation()}
                                                />
                                            )}
                                            <Button.Default
                                                color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                                bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                label={t('busReservation.shuttleRunner.changeChoice')}
                                                className={styles.button}
                                                onPress={() => setIsModifying(true)}
                                            />
                                        </div>
                                    ) : canModify === OpenStatusEnum.SOON ? (
                                        <Font.OxaniumRegular as="span" mobile="14" desktop="16" style={{ textAlign: 'center' }}>
                                            {t('busReservation.shuttleRunner.openSoon')}
                                        </Font.OxaniumRegular>
                                    ) : (
                                        <Font.OxaniumRegular as="span" mobile="14" desktop="16" style={{ textAlign: 'center' }}>
                                            {t('busReservation.shuttleRunner.closed')}
                                        </Font.OxaniumRegular>
                                    )}
                                </div>
                                {(shuttleLines?.filter((shuttleLine) => shuttleLine.selectedShuttleId)?.length ?? 0) > 0 && eventView && (
                                    <div className={styles.formItem}>
                                        <PdfRunnerShuttleButton shuttleLines={shuttleLines} year={year} eventView={eventView} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <Formik
                            initialValues={{
                                shuttleLines: shuttleLines,
                            }}
                            validationSchema={Yup.object().shape({
                                shuttleLines: Yup.array().of(
                                    Yup.object().shape({
                                        selectedShuttleId: Yup.number()
                                            .nullable()
                                            .required(`${t('busReservation.shuttleRunner.errorMustChoose')}`),
                                    })
                                ),
                            })}
                            enableReinitialize={true}
                            onSubmit={(values) => handleReservation(values)}
                        >
                            {({ values, dirty, isValid }) => (
                                <Form className={[styles.form, styles.formRunner].join(' ')}>
                                    {!isLoading &&
                                        values &&
                                        values.shuttleLines &&
                                        values.shuttleLines.length > 0 &&
                                        values.shuttleLines.map((shuttleLine, index) => (
                                            <div key={index} className={styles.formRow}>
                                                <div className={styles.formItem}>
                                                    <div className={styles.input}>
                                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                            {t(`busReservation.shuttleLine.${shuttleLine.type}`)}
                                                        </Font.OxaniumRegular>
                                                        <Font.OxaniumRegular as="span" mobile="14" desktop="16">
                                                            {shuttleLine.name}
                                                        </Font.OxaniumRegular>
                                                    </div>
                                                </div>
                                                <div className={styles.formItem}>
                                                    <div className={styles.input}>
                                                        <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                                                            <label>{t('busReservation.shuttle')} *</label>
                                                        </Font.OxaniumRegular>
                                                        <Field
                                                            as="select"
                                                            name={`shuttleLines[${index}].selectedShuttleId`}
                                                            value={shuttleLine.selectedShuttleId}
                                                        >
                                                            <option value="">- {t('busReservation.shuttleRunner.choose')} -</option>
                                                            <option value={0}>{t('busReservation.empty')}</option>
                                                            {shuttleLine.shuttles.map((shuttle, index) => (
                                                                <option key={index} value={shuttle.id} disabled={shuttle.available <= 0}>
                                                                    {shuttle.name} {shuttle.schedule} (
                                                                    {t('busReservation.availableShuttles', { count: shuttle.available, ordinal: true })})
                                                                </option>
                                                            ))}
                                                        </Field>
                                                        <div className={styles.errorMessage}>
                                                            <ErrorMessage name={`shuttleLines[${index}].selectedShuttleId`} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                    <div className={styles.formRow}>
                                        <div className={styles.formItem}>
                                            <Button.Default
                                                color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                                bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                type="submit"
                                                label={t('busReservation.confirm')}
                                                className={styles.button}
                                                disabled={!dirty || !isValid}
                                            />
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    )}
                </section>
            )}
        </>
    );
};
